import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { notificationsSocketService } from "@/modules/dashboard/notifications/services/notifications-socket.service";
import { useUrlParamMirror } from "@/shared/hooks/useUrlParamMirror";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { createAccessLatch } from "@/shared/utils/accessLatch";

import {
  LIST_KPI_FILTERS,
  LIST_PAGE_SIZE,
  LIST_POLL_MS,
  type ListKpiFilterId,
} from "../constants/payoutBatches.constants";
import { PayoutBatchesService } from "../services/payoutBatches.service";
import type { PayoutBatch, PayoutBatchStats } from "../types/payoutBatches.types";
import { splitCorridor } from "../utils/batchSetupSchema";
import { isLiveStatus } from "../utils/batchStatus";

const listAccessLatch = createAccessLatch();

/** Pushes arrive per processed row while a batch runs; the list re-reads at most this often on their account. */
const SOCKET_REFRESH_MIN_MS = 5_000;

const KPI_IDS = new Set<string>(LIST_KPI_FILTERS.map((filter) => filter.id));

export interface UsePayoutBatchesListResult {
  /** INTERNAL only: the organization whose batches are listed; null = every organization. */
  organizationId: string | null;
  setOrganizationId: (organizationId: string | null) => void;
  kpi: ListKpiFilterId;
  setKpi: (kpi: ListKpiFilterId) => void;
  /** `ISO3:RAIL` or null for every corridor. */
  corridor: string | null;
  setCorridor: (corridor: string | null) => void;
  hasActiveFilters: boolean;
  clearFilters: () => void;
  page: number;
  setPage: (page: number) => void;
  rows: PayoutBatch[];
  total: number;
  stats: PayoutBatchStats | null;
  isLoading: boolean;
  isRefreshing: boolean;
  lastUpdatedAt: Date | null;
  hasError: boolean;
  hasAccess: boolean;
  refresh: () => Promise<void>;
}

/**
 * The batches list with its KPI strip. Filters live in the URL (`org`, `kpi`, `corridor`, `page`), the KPI
 * tile decides the `status` filter — a tile that groups two statuses issues one request per status and merges
 * them, since the endpoint filters by a single status — and the page re-reads every 15 s while a visible
 * batch is live, plus once per burst of socket pushes.
 */
export function usePayoutBatchesList(): UsePayoutBatchesListResult {
  const { user, isInternalUser } = useAuth();
  const ownOrganizationId = user?.organization_id ?? user?.organization?.id ?? null;
  const deniedKey = `${user?.id ?? "anon"}:${ownOrganizationId ?? "no-org"}`;
  const hasAccess = !listAccessLatch.useDenied(deniedKey);
  const canQuery = !!isInternalUser || !!ownOrganizationId;

  const [searchParams] = useSearchParams();
  const [organizationId, setOrganizationIdState] = useState<string | null>(() =>
    isInternalUser ? searchParams.get("org") : null,
  );
  const [kpi, setKpiState] = useState<ListKpiFilterId>(() => {
    const raw = searchParams.get("kpi");
    return raw && KPI_IDS.has(raw) ? (raw as ListKpiFilterId) : "all";
  });
  const [corridor, setCorridorState] = useState<string | null>(() => {
    const raw = searchParams.get("corridor");
    return raw && splitCorridor(raw) ? raw : null;
  });
  const [page, setPageState] = useState(() => {
    const raw = Number(searchParams.get("page"));
    return Number.isInteger(raw) && raw > 1 ? raw : 1;
  });

  useUrlParamMirror("org", organizationId);
  useUrlParamMirror("kpi", kpi === "all" ? null : kpi);
  useUrlParamMirror("corridor", corridor);
  useUrlParamMirror("page", page > 1 ? String(page) : null);

  const [rows, setRows] = useState<PayoutBatch[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<PayoutBatchStats | null>(null);
  const [settledQueryKey, setSettledQueryKey] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [hasError, setHasError] = useState(false);
  const requestSeq = useRef(0);

  const kpiFilter = LIST_KPI_FILTERS.find((filter) => filter.id === kpi) ?? LIST_KPI_FILTERS[0];
  const corridorParts = corridor ? splitCorridor(corridor) : null;
  const queryKey = useMemo(
    () => [organizationId ?? "*", kpi, corridor ?? "*", page].join("|"),
    [corridor, kpi, organizationId, page],
  );
  const isLoading = canQuery && hasAccess && settledQueryKey !== queryKey;

  const fetchPage = useCallback(
    async (background: boolean) => {
      if (!canQuery || listAccessLatch.isDenied(deniedKey)) return;
      const seq = ++requestSeq.current;
      if (background) setIsRefreshing(true);
      else setHasError(false);

      const common = {
        skip: (page - 1) * LIST_PAGE_SIZE,
        limit: LIST_PAGE_SIZE,
        organizationId: isInternalUser ? (organizationId ?? undefined) : undefined,
        rail: corridorParts?.rail,
        country: corridorParts?.country,
      };

      try {
        const [pages, statsResponse] = await Promise.all([
          Promise.all(
            (kpiFilter.statuses ?? [undefined]).map((status) =>
              PayoutBatchesService.list({ ...common, status }).then((response) => response.data),
            ),
          ),
          PayoutBatchesService.stats(isInternalUser ? (organizationId ?? undefined) : undefined).then(
            (response) => response.data,
          ),
        ]);
        if (seq !== requestSeq.current) return;
        const merged = pages
          .flatMap((entry) => entry.data)
          .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
        setRows(merged);
        setTotal(pages.reduce((sum, entry) => sum + entry.total, 0));
        setStats(statsResponse);
        setHasError(false);
        setLastUpdatedAt(new Date());
        setSettledQueryKey(queryKey);
      } catch (err: unknown) {
        if (seq !== requestSeq.current) return;
        if (HttpApiService.isAccessDeniedError(err)) {
          listAccessLatch.latch(deniedKey);
        } else if (!background) {
          setHasError(true);
          setRows([]);
          setTotal(0);
          setSettledQueryKey(queryKey);
        }
      } finally {
        if (seq === requestSeq.current) setIsRefreshing(false);
      }
    },
    [
      canQuery,
      corridorParts?.country,
      corridorParts?.rail,
      deniedKey,
      isInternalUser,
      kpiFilter.statuses,
      organizationId,
      page,
      queryKey,
    ],
  );

  const fetchPageRef = useRef(fetchPage);
  useEffect(() => {
    fetchPageRef.current = fetchPage;
  });

  useEffect(() => {
    void fetchPageRef.current(false);
  }, [queryKey, canQuery, deniedKey]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(total / LIST_PAGE_SIZE));
    if (page > maxPage) setPageState(maxPage);
  }, [page, total]);

  const hasLiveRows = rows.some((row) => isLiveStatus(row.status));
  useEffect(() => {
    if (!hasLiveRows || !hasAccess || !canQuery) return;
    const interval = setInterval(() => void fetchPageRef.current(true), LIST_POLL_MS);
    return () => clearInterval(interval);
  }, [hasLiveRows, hasAccess, canQuery]);

  useEffect(() => {
    if (!hasAccess || !canQuery) return;
    let lastRefresh = 0;
    let trailing: ReturnType<typeof setTimeout> | null = null;
    const refreshNow = () => {
      lastRefresh = Date.now();
      void fetchPageRef.current(true);
    };
    // Throttled with a trailing read: the last push of a burst (a submission right after a selection change) is
    // the one that must land, or the "to approve" tile stays stale.
    const unsubscribe = notificationsSocketService.onPayoutBatchUpdate(() => {
      const wait = SOCKET_REFRESH_MIN_MS - (Date.now() - lastRefresh);
      if (wait <= 0) {
        refreshNow();
        return;
      }
      if (trailing) return;
      trailing = setTimeout(() => {
        trailing = null;
        refreshNow();
      }, wait);
    });
    return () => {
      unsubscribe();
      if (trailing) clearTimeout(trailing);
    };
  }, [hasAccess, canQuery]);

  const setOrganizationId = useCallback((next: string | null) => {
    setOrganizationIdState(next);
    setPageState(1);
  }, []);
  const setKpi = useCallback((next: ListKpiFilterId) => {
    setKpiState(next);
    setPageState(1);
  }, []);
  const setCorridor = useCallback((next: string | null) => {
    setCorridorState(next);
    setPageState(1);
  }, []);
  const clearFilters = useCallback(() => {
    setOrganizationIdState(null);
    setKpiState("all");
    setCorridorState(null);
    setPageState(1);
  }, []);
  const refresh = useCallback(() => fetchPageRef.current(true), []);

  return {
    organizationId,
    setOrganizationId,
    kpi,
    setKpi,
    corridor,
    setCorridor,
    hasActiveFilters: organizationId !== null || kpi !== "all" || corridor !== null,
    clearFilters,
    page,
    setPage: setPageState,
    rows,
    total,
    stats,
    isLoading,
    isRefreshing,
    lastUpdatedAt,
    hasError,
    hasAccess: hasAccess && canQuery,
    refresh,
  };
}
